/**
 * Helpers for the identity-keyed page-image endpoint
 * (`/pages/{page_id}/img?metadata=...&w={bucket}`).
 *
 * The backend serves fixed width buckets so derivative caches are shared and
 * responses are immutable-cacheable; the client's job is only to pick the
 * smallest bucket that covers the pixels it will actually display.
 */

/** Width buckets served by the backend (must match _DERIVATIVE_BUCKETS). */
export const PAGE_IMAGE_BUCKETS = [160, 680, 1360] as const;

export type PageImageWidth = (typeof PAGE_IMAGE_BUCKETS)[number] | 'full';

/**
 * Pick the smallest bucket that covers `displayCssWidth` at the device pixel
 * ratio, falling back to the full-resolution raster beyond the largest bucket.
 */
export function selectPageImageWidth(
    displayCssWidth: number,
    devicePixelRatio: number = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1,
): PageImageWidth {
    const neededPx = displayCssWidth * devicePixelRatio;
    for (const bucket of PAGE_IMAGE_BUCKETS) {
        if (neededPx <= bucket) {
            return bucket;
        }
    }
    return 'full';
}

/**
 * Rewrite the `w=` param of an identity page-image URL. Legacy index-based
 * URLs (`/pages/full/…`, `/pages/thumb/…`) pass through unchanged so callers
 * can apply this unconditionally during the transition.
 */
export function withPageImageWidth(url: string, width: PageImageWidth): string {
    if (!url.includes('/img?')) {
        return url;
    }
    if (/[?&]w=[^&]*/.test(url)) {
        return url.replace(/([?&])w=[^&]*/, `$1w=${width}`);
    }
    return `${url}&w=${width}`;
}
